import { DateOnly } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import { Box, CircularProgress } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { CsvExportButton } from '@src/components/common/CsvExportButton'
import {
  AggregationPeriod,
  AmountMetricKey,
  CountMetricKey,
  MerchantLoanActivityMetric,
  MetricDisplayMode,
  MetricKey,
} from '@src/types/MerchantStatistic'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useComputeLoanActivityMetricsAggregation } from '../merchant-statistics-hooks'
import { LoanMetricsAggregationToggles } from './LoanActivityMetricsAggregationToggles'
import { LoanActivityMetricsValueToggle } from './LoanActivityMetricsValueToggle'

interface MerchantLoanActivityMetricsChartProps {
  merchantLoanActivityMetrics: MerchantLoanActivityMetric[]
  isLoading: boolean
}

export const MerchantLoanActivityMetricsChart = ({
  merchantLoanActivityMetrics,
  isLoading,
}: MerchantLoanActivityMetricsChartProps) => {
  const { t, i18n } = useTranslation()

  const [aggregation, setAggregation] = useState<AggregationPeriod>(AggregationPeriod.Daily)
  const [displayMode, setDisplayMode] = useState<MetricDisplayMode>(MetricDisplayMode.Count)

  const aggregatedData = useComputeLoanActivityMetricsAggregation(merchantLoanActivityMetrics, aggregation)

  const countMetricsOrder: CountMetricKey[] = [
    'totalApplications',
    'totalCreatedLoans',
    'totalApprovedApplications',
    'totalPromoApplicationsWorksheet',
  ]

  const amountMetricsOrder: AmountMetricKey[] = [
    'totalApplicationsAmount',
    'totalGrantedAmount',
    'totalMaxApprovalAmount',
    'totalPromoApprovedWorksheetsAmountAfterMdr',
  ]

  const activeMetricsOrder: MetricKey[] =
    displayMode === MetricDisplayMode.Count ? countMetricsOrder : amountMetricsOrder

  const METRICS: Record<MetricKey, { label: string; color: string }> = {
    totalApplications: { label: t('merchantStatistics.merchantLoanActivity.totalApplications'), color: '#1976d2' },
    totalCreatedLoans: { label: t('merchantStatistics.merchantLoanActivity.totalCreatedLoans'), color: '#2e7d32' },
    totalApprovedApplications: {
      label: t('merchantStatistics.merchantLoanActivity.totalApprovedApplications'),
      color: '#ed6c02',
    },
    totalPromoApplicationsWorksheet: {
      label: t('merchantStatistics.merchantLoanActivity.totalPromoApplicationsWorksheet'),
      color: '#9c27b0',
    },
    totalApplicationsAmount: {
      label: t('merchantStatistics.merchantLoanActivity.totalApplicationsAmount'),
      color: '#1976d2',
    },
    totalGrantedAmount: {
      label: t('merchantStatistics.merchantLoanActivity.totalGrantedAmount'),
      color: '#2e7d32',
    },
    totalMaxApprovalAmount: {
      label: t('merchantStatistics.merchantLoanActivity.totalMaxApprovalAmount'),
      color: '#ed6c02',
    },
    totalPromoApprovedWorksheetsAmountAfterMdr: {
      label: t('merchantStatistics.merchantLoanActivity.totalPromoApprovedWorksheetsAmountAfterMdr'),
      color: '#9c27b0',
    },
  }

  const csvHeaders = [
    t('merchantStatistics.merchantLoanActivity.dateColumn', { defaultValue: 'Date' }),
    ...activeMetricsOrder.map((metric) => METRICS[metric].label),
  ]

  const csvRows = aggregatedData.map((stat) => {
    const csvDate = stat.date
    const metricValues = activeMetricsOrder.map((metric) => stat[metric] ?? 0)
    return [csvDate, ...metricValues]
  })

  const yAxisLabel =
    displayMode === MetricDisplayMode.Count
      ? t('merchantStatistics.merchantLoanActivity.loanActivityYLabel')
      : t('merchantStatistics.merchantLoanActivity.loanActivityAmountYLabel')

  const formatCountValue = (value: number) => new Intl.NumberFormat(i18n.language).format(value)

  const series = activeMetricsOrder.map((metric) => ({
    label: METRICS[metric].label,
    data: aggregatedData.map((stat) => stat[metric]),
    color: METRICS[metric].color,
    valueFormatter: (value: number | null) =>
      value == null ? '' : displayMode === MetricDisplayMode.Amount ? formatCurrency(value) : formatCountValue(value),
  }))

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', height: 600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
          <LoanMetricsAggregationToggles aggregation={aggregation} onAggregationChange={setAggregation} />
          <LoanActivityMetricsValueToggle value={displayMode} onChange={setDisplayMode} />
        </Box>
        <CsvExportButton
          variant="outlined"
          filename={`loan-activity-${displayMode}-${aggregation}.csv`}
          headers={csvHeaders}
          rows={csvRows}
          disabled={!aggregatedData.length}
        />
      </Box>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <LineChart
          xAxis={[
            {
              data: aggregatedData.map((stat) => new DateOnly(stat.date)),
              label: t('merchantStatistics.merchantLoanActivity.loanActivityXLabel'),
              valueFormatter: (timestamp: number) =>
                new DateOnly(new Date(timestamp)).toLocaleDateString(i18n.language, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                }),
            },
          ]}
          yAxis={[
            {
              label: yAxisLabel,
              valueFormatter: (value: number | null) =>
                value == null
                  ? ''
                  : displayMode === MetricDisplayMode.Amount
                    ? formatCurrency(value)
                    : formatCountValue(value),
            },
          ]}
          series={series}
          height={600}
          grid={{ vertical: true, horizontal: true }}
        />
      </Box>
    </Box>
  )
}
